import 'antd/dist/reset.css';
import React, { FC, PropsWithChildren } from 'react';
import { App, ConfigProvider, theme as antdTheme } from 'antd';
import { ConfigProviderProps } from 'antd/es/config-provider';
import antDLocaleET from 'antd/es/locale/et_EE';
import {
  colorLink,
  colorLinkActive,
  colorLinkHover,
  colorPrimaryBorder,
  components,
  fontSizeSM,
  fontWeightNormal,
  fontWeightStrong,
  letterSpacing,
  letterSpacingHeading1,
  letterSpacingHeading2,
  letterSpacingHeading3,
  letterSpacingHeading4,
  letterSpacingHeading5,
  letterSpacingSM,
  letterSpacingStrong,
  lineHeightPx,
  token,
} from './tokens';

export interface AtlasThemeProviderProps
  extends PropsWithChildren, ConfigProviderProps {}

export const AtlasThemeProvider: FC<AtlasThemeProviderProps> = (props) => {
  const { children, ...rest } = props;
  return (
    <ConfigProvider
      theme={theme}
      renderEmpty={renderEmpty}
      locale={antDLocaleET}
      componentSize="middle"
      {...rest}
    >
      <CustomApp>{children}</CustomApp>
    </ConfigProvider>
  );
};

const theme = {
  algorithm: antdTheme.defaultAlgorithm,
  token,
  components,
};

const renderEmpty = () => <></>;

/**
 * We need to wrap app contents with antd's App component
 * to make sure that theme fontFamily is applied to all components.
 * See https://github.com/ant-design/ant-design/issues/39327
 */
const CustomApp: FC<PropsWithChildren> = ({ children }) => {
  const { token } = antdTheme.useToken();
  return (
    <>
      <style>{`
        body {
          font-size: 14px;
          font-family: ${token.fontFamily};
          font-size: ${token.fontSize}px;
          line-height: ${token.lineHeight};
          letter-spacing: ${letterSpacing}em;
        }

        input {
          letter-spacing: ${letterSpacing}em;
        }

        h1 {
          font-size: ${token.fontSizeHeading1}px;
          font-weight: ${token.fontWeightStrong};
          line-height: ${token.lineHeightHeading1};
          letter-spacing: ${letterSpacingHeading1}em;
          margin: 0;
        }
        h2 {
          font-size: ${token.fontSizeHeading2}px;
          font-weight: ${token.fontWeightStrong};
          line-height: ${token.lineHeightHeading2};
          letter-spacing: ${letterSpacingHeading2}em;
          margin: 0;
        }
        h3 {
          font-size: ${token.fontSizeHeading3}px;
          font-weight: ${token.fontWeightStrong};
          line-height: ${token.lineHeightHeading3};
          letter-spacing: ${letterSpacingHeading3}em;
          margin: 0;
        }
        h4 {
          font-size: ${token.fontSizeHeading4}px;
          font-weight: ${token.fontWeightStrong};
          line-height: ${token.lineHeightHeading4};
          letter-spacing: ${letterSpacingHeading4}em;
          margin: 0;
        }
        h5 {
          font-size: ${token.fontSizeHeading5}px;
          font-weight: ${token.fontWeightStrong};
          line-height: ${token.lineHeightHeading5};
          letter-spacing: ${letterSpacingHeading5}em;
          margin: 0;
        }
        small {
          font-size: ${token.fontSizeSM}px;
          line-height: ${token.lineHeightSM};
          letter-spacing: ${letterSpacingSM}em;
        }
        strong {
          font-weight: ${token.fontWeightStrong};
          letter-spacing: ${letterSpacingStrong}em;
        }
        a {
          color: ${colorLink};
          text-decoration: none;
          outline: none;
          cursor: pointer;
          transition: all 0.3s;
          border: 0;
          padding: 0;
          background: none;
          user-select: none;
        }
        a:active {
          color: ${colorLinkActive};
        }
        a:focus,
        a:hover {
          color: ${colorLinkHover};
        }
        a:focus-visible {
          outline: 3px solid ${colorPrimaryBorder};
          outline-offset: 1px;
          transition: outline-offset 0s, outline 0s;
        }

        /* Button */
        .ant-btn {
          letter-spacing: ${letterSpacingStrong}em;
          display: inline-flex;
          justify-content: center;
          align-items: center;
        }
        .ant-btn.ant-btn-link:not(:disabled):not(.ant-btn-disabled):hover {
          background-color: ${token.colorLink}10;
        }

        /* Divider */
        .ant-divider-horizontal.ant-divider-with-text {
          font-size: ${token.fontSizeSM}px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        /* Result */
        .ant-result .ant-result-title {
          font-weight: ${token.fontWeightStrong};
        }

        /* Segmented */
        .ant-segmented-item-label .ant-badge-count {
          background-color: ${token.colorBgBase};
          color: ${token.colorTextLabel};
        }
        .ant-segmented-item-selected .ant-badge-count {
          background-color: ${token.colorBgTextHover};
        }

        /* Table */
        .ant-table-thead > tr > th,
        .ant-table-thead > tr > td {
          font-size: ${fontSizeSM}px;
          font-weight: ${fontWeightStrong};
          letter-spacing: ${letterSpacingSM}em;
        }
        .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before,
        .ant-table-thead > tr > td:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
          height: ${lineHeightPx}px;
        }

        /* Tag */
        .ant-tag {
          font-size: 11px;
          font-weight: ${token.fontWeightStrong};
          letter-spacing: 0.1em;
        }

        /* Tooltip */
        .ant-tooltip-inner {
          max-height: 285px;
          overflow: auto;
          scrollbar-color: rgba(255, 255, 255, .7) rgba(255, 255, 255, .2);
        }

        /* Flex */
        [class^='ant-flex'] {
          font-size: unset;
        }

        /* PopConfirm */
        .ant-popconfirm-message .ant-popconfirm-title {
          font-weight: ${fontWeightNormal};
        }

        /* Descriptions */
        .ant-descriptions .ant-descriptions-item-label {
          font-size: ${fontSizeSM}px;
          line-height: ${lineHeightPx}px;
        }
        .ant-descriptions .ant-descriptions-item-label:after {
          position: static;
        }

        .ant-popconfirm-message .ant-popconfirm-description {
          font-weight: ${fontWeightStrong};
        }

        /* Allow select dropdown to grow with its content.
        Otherwise select dropdown would be as wide as select itself and in some cases clips option labels with ellipsis too eagerly */
        .rc-virtual-list-holder-inner {
          position: unset !important;
        }

        .ant-dropdown {
          > ul {
            > li {
              padding: 0 !important;
            }
          }
        }
      `}</style>
      <App>{children}</App>
    </>
  );
};
